// src/modulos/Parametros/ColorsPage.js
import React, { useState, useMemo, useCallback } from 'react';
import { collection, query, where, limit, getDocs } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import useFirestore from '../../hooks/useFirestore';
import usePagePermissions from '../../hooks/usePagePermissions';

// Import global components
import {
    PageHeader,
    SearchBar,
    FormModal,
    ConfirmModal,
    TableSkeleton,
    EmptyState,
    Pagination,
} from '../../components/common';

// Import utilities
import { getTimestamps } from '../../utils/timestamps';

// Page-specific styles
import styles from './Parameters.module.css';

// Constantes
const COLORS_COLLECTION = 'parameters/data/cores';
const ITEMS_PER_PAGE = 10;

const ColorsPage = () => {
    const can = usePagePermissions('cores');

    // Estados Locais
    const [editingItem, setEditingItem] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [nameInput, setNameInput] = useState('');
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, name: '' });
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Hook de Dados
    const {
        data: colors,
        loading: listLoading,
        error: listError,
        addDocument,
        updateDocument,
        deleteDocument,
        refreshData
    } = useFirestore(COLORS_COLLECTION, 'name');

    // ============================================================
    // 1. DEFINIR filteredColors ANTES de ser usado nos callbacks
    // ============================================================
    const filteredColors = useMemo(() => {
        if (!colors) return [];
        if (!searchTerm) return colors;

        const lowerCaseSearch = searchTerm.toLowerCase();
        return colors.filter(item =>
            item.name.toLowerCase().includes(lowerCaseSearch)
        );
    }, [colors, searchTerm]);

    const totalFilteredItems = filteredColors.length;
    const totalPagesAfterFilter = Math.ceil(totalFilteredItems / ITEMS_PER_PAGE);

    const currentColors = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredColors.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredColors, currentPage]);

    // ============================================================
    // 2. Função de verificação de dependências
    // ============================================================
    const checkColorDependencies = useCallback(async (colorId, colorName) => {
        try {
            const productsRef = collection(db, 'products');
            const productsQuery = query(productsRef, where('cor', '==', colorName), limit(1));
            const productsSnapshot = await getDocs(productsQuery);

            const rawMaterialsRef = collection(db, 'raw-materials');
            const rawMaterialsQuery = query(rawMaterialsRef, where('cor', '==', colorName), limit(1));
            const rawMaterialsSnapshot = await getDocs(rawMaterialsQuery);

            const productVariantsRef = collection(db, 'product-variants');
            const productVariantsQuery = query(productVariantsRef, where('cor', '==', colorName), limit(1));
            const productVariantsSnapshot = await getDocs(productVariantsQuery);

            const hasProducts = !productsSnapshot.empty;
            const hasRawMaterials = !rawMaterialsSnapshot.empty;
            const hasVariants = !productVariantsSnapshot.empty;

            if (hasProducts || hasRawMaterials || hasVariants) {
                let message = `❌ Não é possível excluir a cor "${colorName}" pois ela está sendo utilizada em:\n`;
                if (hasProducts) message += `\n• Produtos cadastrados`;
                if (hasRawMaterials) message += `\n• Matérias-primas`;
                if (hasVariants) message += `\n• Variantes de produtos`;
                message += `\n\n⚠️ Remova as referências primeiro antes de excluir esta cor.`;
                return { canDelete: false, message };
            }

            return { canDelete: true, message: '' };
        } catch (error) {
            console.error('Erro ao verificar dependências:', error);
            return { canDelete: false, message: 'Erro ao verificar se a cor pode ser excluída. Tente novamente.' };
        }
    }, []);

    // ============================================================
    // 3. Reset e Modal
    // ============================================================
    const resetForm = useCallback(() => {
        setEditingItem(null);
        setNameInput('');
        setFormError('');
        setFormSuccess('');
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        resetForm();
    }, [resetForm]);

    // ============================================================
    // 4. Funções CRUD
    // ============================================================
    const handleNewClick = useCallback(() => {
        resetForm();
        setIsModalOpen(true);
    }, [resetForm]);

    const handleEditClick = useCallback((item) => {
        setEditingItem(item);
        setNameInput(item.name);
        setFormError('');
        setFormSuccess('');
        setIsModalOpen(true);
    }, []);

    const handleDeleteClick = useCallback((id, name) => {
        setDeleteConfirm({ isOpen: true, id, name });
    }, []);

    // ============================================================
    // 5. Confirmar Delete (AGORA filteredColors já está definido)
    // ============================================================
    const handleConfirmDelete = useCallback(async () => {
        const { id, name } = deleteConfirm;
        setDeleteLoading(true);

        try {
            const { canDelete, message } = await checkColorDependencies(id, name);

            if (!canDelete) {
                alert(message);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });
                setDeleteLoading(false);
                return;
            }

            const result = await deleteDocument(id);

            if (result.success) {
                setFormSuccess(`✅ Cor "${name}" excluída com sucesso!`);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });

                // Usar filteredColors (já definido acima)
                const remainingItems = filteredColors.length - 1;
                const maxPage = Math.ceil(remainingItems / ITEMS_PER_PAGE);
                if (currentPage > maxPage && maxPage > 0) {
                    setCurrentPage(maxPage);
                } else if (remainingItems === 0) {
                    setCurrentPage(1);
                }

                setTimeout(() => setFormSuccess(''), 3000);
            } else {
                setFormError("❌ Erro ao deletar Cor: " + result.error);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });
            }
        } catch (error) {
            setFormError('Erro ao deletar cor. Tente novamente.');
            setDeleteConfirm({ isOpen: false, id: null, name: '' });
        } finally {
            setDeleteLoading(false);
        }
    }, [deleteConfirm, checkColorDependencies, deleteDocument, filteredColors, currentPage]);

    // ============================================================
    // 6. Submissão do formulário
    // ============================================================
    const handleFormSubmit = useCallback(async (e) => {
        e.preventDefault();
        setFormError('');
        setFormSuccess('');

        if (!nameInput.trim()) {
            setFormError("O nome da Cor é obrigatório.");
            return;
        }

        const userEmail = localStorage.getItem('userEmail') || 'system';
        const timestamps = getTimestamps(editingItem, userEmail);
        const data = {
            name: nameInput.trim(),
            ...timestamps,
        };

        setFormLoading(true);

        try {
            let result;
            if (editingItem) {
                result = await updateDocument(editingItem.id, data);
            } else {
                result = await addDocument(data);
            }

            if (result.success) {
                setFormSuccess(`✅ Cor ${editingItem ? 'editada' : 'adicionada'} com sucesso!`);
                setTimeout(() => {
                    handleCloseModal();
                }, 1500);
            } else {
                setFormError(`❌ Falha ao ${editingItem ? 'editar' : 'adicionar'}: ${result.error}`);
            }
        } catch (error) {
            setFormError('Erro ao salvar no banco de dados. Tente novamente.');
        } finally {
            setFormLoading(false);
        }
    }, [nameInput, editingItem, addDocument, updateDocument, handleCloseModal]);

    // ============================================================
    // 7. Paginação e Busca
    // ============================================================
    const handlePageChange = useCallback((newPage) => {
        if (newPage > 0 && newPage <= totalPagesAfterFilter) {
            setCurrentPage(newPage);
        }
    }, [totalPagesAfterFilter]);

    const handleSearch = useCallback((term) => {
        setSearchTerm(term);
        setCurrentPage(1);
    }, []);

    // ============================================================
    // 8. Renderização
    // ============================================================
    return (
        <div className={styles.pageContainer}>
            {/* Form Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleFormSubmit}
                title={editingItem ? `Editar: ${editingItem.name}` : 'Adicionar Nova Cor'}
                subtitle={editingItem ? `Editando: ${editingItem.name}` : undefined}
                isLoading={formLoading}
                error={formError}
                success={formSuccess}
                submitText={editingItem ? '💾 Salvar Edição' : '✨ Adicionar Cor'}
            >
                <div className={styles.inputGroup}>
                    <label htmlFor="name">
                        Nome da Cor <span className={styles.required}>*</span>
                    </label>
                    <input
                        id="name"
                        type="text"
                        value={nameInput}
                        onChange={(e) => setNameInput(e.target.value)}
                        placeholder="Ex: Vermelho, Azul Marinho, Verde Musgo"
                        required
                        className={styles.input}
                        disabled={formLoading}
                        autoFocus
                    />
                    <small className={styles.fieldHint}>
                        Digite o nome da cor como será exibido nos produtos
                    </small>
                </div>
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null, name: '' })}
                onConfirm={handleConfirmDelete}
                title="⚠️ Confirmar Exclusão"
                message={`Tem certeza que deseja deletar a cor "${deleteConfirm.name}"?\n\nEsta ação não poderá ser desfeita e pode afetar a integridade dos dados.`}
                isLoading={deleteLoading}
                danger={true}
            />

            {/* Page Header */}
            <PageHeader
                title="Cores"
                actionText="Adicionar Cor"
                onAction={handleNewClick}
                disabled={formLoading}
                showAction={can.create}
            />

            {/* Search Bar */}
            <div className={styles.searchBar}>
                <SearchBar
                    placeholder="Buscar por nome da cor..."
                    onSearch={handleSearch}
                    disabled={listLoading || formLoading}
                />
            </div>

            {/* Error State */}
            {listError && (
                <div className={styles.errorContainer}>
                    <div className={styles.errorIcon}>⚠️</div>
                    <p className={styles.errorText}>Erro ao carregar lista: {listError}</p>
                    <button onClick={refreshData} className={styles.retryButton}>
                        Tentar novamente
                    </button>
                </div>
            )}

            {/* Table or Loading State */}
            {!listError && (
                <>
                    {listLoading ? (
                        <TableSkeleton columns={2} rows={ITEMS_PER_PAGE} />
                    ) : currentColors.length === 0 ? (
                        <EmptyState
                            icon={searchTerm ? '🔍' : '🎨'}
                            title={
                                searchTerm
                                    ? 'Nenhum resultado encontrado'
                                    : 'Nenhuma cor cadastrada'
                            }
                            message={
                                searchTerm
                                    ? `Nenhuma cor corresponde a "${searchTerm}"`
                                    : "Clique em 'Adicionar Cor' para começar"
                            }
                        />
                    ) : (
                        <>
                            <div className={styles.tableContainer}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Cor</th>
                                            <th style={{ width: '180px' }}>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentColors.map((item) => (
                                            <tr key={item.id} className={styles.tableRow}>
                                                <td className={styles.conditionName}>
                                                    <strong>{item.name}</strong>
                                                    {item.updatedAt && (
                                                        <small className={styles.rowMetadata}>
                                                            Última atualização: {new Date(item.updatedAt).toLocaleDateString('pt-BR')}
                                                        </small>
                                                    )}
                                                </td>
                                                <td className={styles.actionsCell}>
                                                    {can.edit && (
                                                        <button
                                                            onClick={() => handleEditClick(item)}
                                                            className={styles.actionButton}
                                                            disabled={formLoading}
                                                        >
                                                            Editar
                                                        </button>
                                                    )}
                                                    {can.delete && (
                                                        <button
                                                            onClick={() => handleDeleteClick(item.id, item.name)}
                                                            className={`${styles.actionButton} ${styles.deleteButton}`}
                                                            disabled={formLoading}
                                                        >
                                                            Deletar
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPagesAfterFilter}
                                totalItems={totalFilteredItems}
                                onPageChange={handlePageChange}
                                disabled={formLoading}
                                itemsLabel="cores"
                            />
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default ColorsPage;